import React, { useState, useEffect } from 'react';
import {
  DollarSign, TrendingUp, AlertTriangle, Calendar,
  PieChart, BarChart3, Percent, Package
} from 'lucide-react';
import './TRMStyles.css';

const FinancialTracking = () => {
  const [summary, setSummary] = useState(null);
  const [byCategory, setByCategory] = useState([]);
  const [byManufacturer, setByManufacturer] = useState([]);
  const [utilization, setUtilization] = useState([]);
  const [expiringContracts, setExpiringContracts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFinancialData();
  }, []);

  const fetchFinancialData = async () => {
    try {
      setLoading(true);
      const [summaryRes, categoryRes, mfgRes, utilRes, expiringRes] = await Promise.all([
        fetch('/api/trm/spend/summary'),
        fetch('/api/trm/spend/by-category'),
        fetch('/api/trm/spend/by-manufacturer'),
        fetch('/api/trm/spend/utilization?max_utilization=80'),
        fetch('/api/trm/spend/contracts/expiring?days_ahead=90')
      ]);

      setSummary(await summaryRes.json());
      setByCategory(await categoryRes.json());
      setByManufacturer(await mfgRes.json());
      setUtilization(await utilRes.json());
      setExpiringContracts(await expiringRes.json());
    } catch (error) {
      console.error('Error fetching financial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  if (loading) {
    return (
      <div className="trm-loading">
        <div className="spinner"></div>
        <p>Loading financial data...</p>
      </div>
    );
  }

  return (
    <div className="trm-financial-dashboard">
      <div className="dashboard-header">
        <h2>💰 Financial Tracking & License Management</h2>
        <p>Monitor software spend, license utilization, and identify cost savings opportunities</p>
      </div>

      {/* Summary Cards */}
      <div className="metric-cards">
        <div className="metric-card primary">
          <div className="metric-icon">
            <DollarSign size={32} />
          </div>
          <div className="metric-content">
            <h3>Total Annual Cost</h3>
            <div className="metric-value">{formatCurrency(summary?.total_annual_cost || 0)}</div>
            <p className="metric-subtitle">Across {summary?.total_products || 0} products</p>
          </div>
        </div>

        <div className="metric-card success">
          <div className="metric-icon">
            <Percent size={32} />
          </div>
          <div className="metric-content">
            <h3>Avg. License Utilization</h3>
            <div className="metric-value">{(summary?.average_utilization_pct || 0).toFixed(1)}%</div>
            <p className="metric-subtitle">{summary?.total_licenses_in_use?.toLocaleString()} / {summary?.total_licenses_purchased?.toLocaleString()} licenses</p>
          </div>
        </div>

        <div className="metric-card warning">
          <div className="metric-icon">
            <AlertTriangle size={32} />
          </div>
          <div className="metric-content">
            <h3>Waste Identified</h3>
            <div className="metric-value">{formatCurrency(summary?.total_waste_cost || 0)}</div>
            <p className="metric-subtitle">From underutilized licenses</p>
          </div>
        </div>

        <div className="metric-card danger">
          <div className="metric-icon">
            <Calendar size={32} />
          </div>
          <div className="metric-content">
            <h3>Contracts Expiring</h3>
            <div className="metric-value">{summary?.contracts_expiring_soon || 0}</div>
            <p className="metric-subtitle">Within 90 days</p>
          </div>
        </div>
      </div>

      {/* Spend by Category */}
      <div className="data-section">
        <div className="section-header">
          <h3><PieChart size={20} /> Spend by Category</h3>
        </div>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Category</th>
                <th className="text-right">Annual Cost</th>
                <th className="text-right">Products</th>
                <th className="text-right">Avg Utilization</th>
              </tr>
            </thead>
            <tbody>
              {byCategory.map((cat, idx) => (
                <tr key={idx}>
                  <td><strong>{cat.trm_category}</strong></td>
                  <td className="text-right">{formatCurrency(cat.annual_cost || 0)}</td>
                  <td className="text-right">{cat.product_count}</td>
                  <td className="text-right">
                    <span className={`utilization-badge ${(cat.utilization_pct || 0) < 70 ? 'low' : 'good'}`}>
                      {(cat.utilization_pct || 0).toFixed(1)}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top Vendors by Spend */}
      <div className="data-section">
        <div className="section-header">
          <h3><BarChart3 size={20} /> Top Vendors by Spend</h3>
        </div>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Vendor</th>
                <th className="text-right">Annual Cost</th>
                <th className="text-right">Products</th>
                <th className="text-right">Total Licenses</th>
              </tr>
            </thead>
            <tbody>
              {byManufacturer.slice(0, 10).map((mfg, idx) => (
                <tr key={idx}>
                  <td><strong>{mfg.manufacturer_name}</strong></td>
                  <td className="text-right">{formatCurrency(mfg.annual_cost || 0)}</td>
                  <td className="text-right">{mfg.product_count}</td>
                  <td className="text-right">{(mfg.total_licenses || 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Underutilized Licenses */}
      <div className="data-section alert-section">
        <div className="section-header">
          <h3><AlertTriangle size={20} /> Underutilized Licenses (Below 80%)</h3>
        </div>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Product</th>
                <th className="text-right">Purchased</th>
                <th className="text-right">In Use</th>
                <th className="text-right">Utilization</th>
                <th className="text-right">Waste Cost</th>
              </tr>
            </thead>
            <tbody>
              {utilization.length > 0 ? (
                utilization.map((item, idx) => (
                  <tr key={idx}>
                    <td><strong>{item.product_name}</strong></td>
                    <td className="text-right">{item.quantity_purchased?.toLocaleString()}</td>
                    <td className="text-right">{item.quantity_in_use?.toLocaleString()}</td>
                    <td className="text-right">
                      <span className="utilization-badge low">
                        {item.utilization_pct?.toFixed(1)}%
                      </span>
                    </td>
                    <td className="text-right text-danger">{formatCurrency(item.waste_cost)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: 'var(--cares-text-note)' }}>
                    ✅ All licenses are well utilized (above 80%)
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Expiring Contracts */}
      <div className="data-section alert-section">
        <div className="section-header">
          <h3><Calendar size={20} /> Contracts Expiring Soon</h3>
        </div>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Contract</th>
                <th>Product</th>
                <th className="text-right">End Date</th>
                <th className="text-right">Days Left</th>
                <th className="text-right">Annual Cost</th>
              </tr>
            </thead>
            <tbody>
              {expiringContracts.length > 0 ? (
                expiringContracts.map((contract, idx) => (
                  <tr key={idx}>
                    <td><strong>{contract.contract_name || contract.contract_number}</strong></td>
                    <td>{contract.product_name}</td>
                    <td className="text-right">{new Date(contract.contract_end_date).toLocaleDateString()}</td>
                    <td className="text-right">
                      <span className={`days-badge ${contract.days_until_expiry < 30 ? 'urgent' : 'warning'}`}>
                        {contract.days_until_expiry} days
                      </span>
                    </td>
                    <td className="text-right">{formatCurrency(contract.annual_cost)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: 'var(--cares-text-note)' }}>
                    ✅ No contracts expiring in the next 90 days
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FinancialTracking;
